import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil, X, Package } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, Btn, Loader } from "@/components/portal/ui";

const empty = { tier: "basic", name: {}, price: 0, currency: "CHF", interval: "month", features: {}, setup_fee: 0, order: 1, active: true };
const TIERS = ["free", "basic", "pro", "premium", "growth", "individual"];

export function PackagesManager() {
  const { t, L, lang } = useI18n();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/packages");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ ...empty, order: (items?.length || 0) + 1 }); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...empty, ...p }); setEditing(p.id); };
  const close = () => { setEditing(null); setForm(empty); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/packages", form);
      else await api.put(`/admin/packages/${editing}`, form);
      toast.success(t("common.saved"));
      close();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/admin/packages/${id}`);
    load();
  };

  const featuresText = (form.features?.[lang] || []).join("\n");
  const setFeatures = (txt) => setForm((f) => ({ ...f, features: { ...f.features, [lang]: txt.split("\n").map((s) => s.trim()).filter(Boolean) } }));

  const st = { background: "var(--bg)", borderColor: "var(--border)", borderRadius: "var(--radius)", color: "var(--text)" };
  if (!items) return <Loader />;

  return (
    <div className="space-y-4" data-testid="packages-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>{items.length} · {t("dash.packages")}</p>
        <Btn onClick={openNew} data-testid="pkgmgr-new-btn"><Plus className="h-4 w-4" /> {t("pkg.new")}</Btn>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <Card key={p.id} className="p-5 kw-fade-up" data-testid={`pkgmgr-item-${p.id}`}>
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full" style={{ background: "var(--surface-alt)", color: "var(--accent)" }}>{p.tier}</span>
              {!p.active && <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full" style={{ background: "var(--surface-alt)", color: "var(--text-muted)" }}>{t("promo.inactive")}</span>}
            </div>
            <div className="font-heading text-xl font-bold mt-2" style={{ color: "var(--text)" }}>{L(p.name)}</div>
            <div className="mt-1 flex items-baseline gap-1">
              <span className="text-3xl font-heading font-bold" style={{ color: "var(--text)" }}>{p.price === 0 ? t("pkg.free") : `${p.currency} ${p.price}`}</span>
              {p.price > 0 && <span className="text-sm" style={{ color: "var(--text-muted)" }}>/{t("pkg.month")}</span>}
            </div>
            <ul className="mt-3 space-y-1">
              {(L(p.features) || []).map((f, i) => <li key={i} className="text-sm" style={{ color: "var(--text-muted)" }}>• {f}</li>)}
            </ul>
            <div className="mt-4 flex items-center gap-2">
              <Btn variant="soft" onClick={() => openEdit(p)} className="h-9 px-3 text-xs" data-testid={`pkgmgr-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /> {t("promo.edit")}</Btn>
              <Btn variant="danger" onClick={() => remove(p.id)} className="ml-auto h-9 px-3" data-testid={`pkgmgr-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Btn>
            </div>
          </Card>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.5)" }} data-testid="pkgmgr-modal">
          <Card className="w-full max-w-lg p-6 max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold flex items-center gap-2" style={{ color: "var(--text)" }}><Package className="h-5 w-5" style={{ color: "var(--accent)" }} /> {editing === "new" ? t("pkg.new") : t("promo.edit")}</h3>
              <button onClick={close} data-testid="pkgmgr-modal-close"><X className="h-5 w-5" style={{ color: "var(--text-muted)" }} /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("pkg.name")} ({lang.toUpperCase()})</label>
                  <input data-testid="pkgmgr-name-input" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                    value={form.name?.[lang] || ""} onChange={(e) => setForm({ ...form, name: { ...form.name, [lang]: e.target.value } })} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("pkg.tier")}</label>
                  <select data-testid="pkgmgr-tier-input" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                    value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })}>
                    {TIERS.map((tr) => <option key={tr} value={tr}>{tr}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("pkg.price")}</label>
                  <input data-testid="pkgmgr-price-input" type="number" min="0" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                    value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>CHF</label>
                  <input data-testid="pkgmgr-currency-input" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                    value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("pkg.setupFee")}</label>
                  <input data-testid="pkgmgr-setup-input" type="number" min="0" className="w-full px-3.5 h-11 text-sm border outline-none" style={st}
                    value={form.setup_fee} onChange={(e) => setForm({ ...form, setup_fee: parseFloat(e.target.value) || 0 })} />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("pkg.features")} ({lang.toUpperCase()})</label>
                <textarea data-testid="pkgmgr-features-input" rows={4} className="w-full px-3.5 py-2.5 text-sm border outline-none" style={st}
                  placeholder={t("pkg.featuresHint")} value={featuresText} onChange={(e) => setFeatures(e.target.value)} />
              </div>
              <div className="flex items-center gap-3">
                <label className="text-xs font-semibold uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>{t("control.status")}</label>
                <select data-testid="pkgmgr-active-input" className="px-3.5 h-10 text-sm border outline-none" style={st}
                  value={form.active ? "1" : "0"} onChange={(e) => setForm({ ...form, active: e.target.value === "1" })}>
                  <option value="1">{t("pkg.active")}</option>
                  <option value="0">{t("promo.inactive")}</option>
                </select>
              </div>
              <Btn onClick={save} disabled={saving || !form.name?.[lang]} className="w-full" data-testid="pkgmgr-save-btn">{t("common.save")}</Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
